import { useCallback, useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { businessApi } from '@/lib/api-paths';
import { api } from '@/lib/api';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { PanelShell } from '@/components/panel-shell';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Modal } from '@/components/modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TERMS } from '@pageboss/shared';

// =====================================================================
// Team — "/app/b/:businessId/team" (Phase 2 / P2.6). The smallest management
// surface over the P2.5/P2.6 team API: members (edit role & scope, remove),
// pending invitations (revoke, resend), and the invite flow whose delivery is
// COPY-LINK ONLY (no e-mail is sent anywhere in this phase).
//
// Controls follow the LOCKED role contract, using the `viewer` standing the
// API returns with the list (P2.7 adds the owner-only Transfer ownership
// control — internal pageboss ownership only):
//   owner    edit + remove on manager/editor rows; revoke + resend on any invite
//   manager  remove on editor rows inside the manager's scope; revoke + resend on
//            editor invites inside that scope; never role/scope editing
//   editor   read-only
// The owner row shows a badge and no per-row controls; ownership moves only
// through the explicit Transfer ownership control (P2.7). Hiding a button is NOT a security boundary — every action is
// re-authorized by the API, and a 403 is surfaced honestly.
//
// A raw invitation token/URL exists in the browser ONLY in the one-time panel
// shown right after a create/resend; it is never stored, never re-shown.
// =====================================================================

type Role = 'owner' | 'manager' | 'editor';

interface Member {
  membershipId: string;
  userId: string;
  email: string;
  name: string | null;
  role: Role;
  locationScope: string[];
  createdAt: string;
}
interface Invitation {
  id: string;
  emailDisplay: string;
  role: Role;
  locationScope: string[];
  expiresAt: string;
  createdAt: string;
}
interface TeamResponse {
  members: Member[];
  invitations: Invitation[];
  seats: { max: number; occupied: number };
  viewer: { membershipId: string; userId: string; role: Role; locationScope: string[] };
}
interface LocationSummary {
  id: string;
  name: string;
  status: string;
}
interface CreatedInvitation {
  invitation: { id: string; emailDisplay: string; role: Role; locationScope: string[]; expiresAt: string };
  token: string;
  inviteUrl: string;
}

/** `[] = whole business` — a target scope is within the viewer's when the viewer is full or holds every id. */
function withinViewer(viewerScope: string[], targetScope: string[]): boolean {
  if (viewerScope.length === 0) return true;
  if (targetScope.length === 0) return false;
  return targetScope.every((id) => viewerScope.includes(id));
}

/** What the API would allow (the API stays authoritative); the UI just avoids offering the obvious 403s. */
export function controlsFor(viewer: TeamResponse['viewer'], target: { role: Role; locationScope: string[] }, kind: 'member' | 'invitation', targetMembershipId?: string) {
  const none = { edit: false, remove: false };
  if (target.role === 'owner') return none;
  if (kind === 'member' && targetMembershipId === viewer.membershipId) return none;
  if (viewer.role === 'owner') return { edit: kind === 'member', remove: true };
  if (viewer.role === 'manager') {
    const ok = target.role === 'editor' && withinViewer(viewer.locationScope, target.locationScope);
    return { edit: false, remove: ok };
  }
  return none;
}

function scopeSummary(scope: string[], locations: LocationSummary[]): string {
  if (scope.length === 0) return 'Whole business';
  return scope.map((id) => locations.find((l) => l.id === id)?.name ?? id).join(', ');
}

function errorCode(err: unknown): string {
  const body = (err as { body?: { code?: string; error?: string } })?.body;
  return body?.code ?? body?.error ?? (err instanceof Error ? err.message : 'request_failed');
}

/** One-time invite link panel. The URL lives only in this component's props for as long as it is mounted. */
function InviteLinkPanel({ created, onDone }: { created: CreatedInvitation; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(created.inviteUrl);
      setCopied(true);
    } catch {
      // the link is visible below; older browsers can select it
    }
  }
  return (
    <section className="rounded-xl border border-border-subtle bg-card p-4">
      <p className="text-sm font-semibold text-foreground">Invite link for {created.invitation.emailDisplay}</p>
      <p className="mt-1 text-xs text-text-secondary">
        Copy it now — it is shown once and expires {new Date(created.invitation.expiresAt).toLocaleDateString()}. Send it to them yourself; no e-mail is sent.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <Input readOnly value={created.inviteUrl} aria-label="Invite link" className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
        <Button type="button" size="sm" onClick={copy}>
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </section>
  );
}

/**
 * Scope picker. "Whole business" is an EXPLICIT choice (`whole`), never the accidental result of
 * unchecking the last location; a restricted manager cannot choose it at all (the API would refuse
 * it as widening). Ids the caller holds that are not in the grantable list (archived, or outside a
 * restricted viewer's own locations) are shown as removable "(no longer grantable)" rows so a scope
 * can always be edited into a valid one. Demo locations are never offered.
 */
export function ScopePicker({ locations, value, whole, allowWhole, onChange, disabled }: { locations: LocationSummary[]; value: string[]; whole: boolean; allowWhole: boolean; onChange: (next: { whole: boolean; ids: string[] }) => void; disabled?: boolean }) {
  const grantable = locations.filter((l) => l.status !== 'demo');
  const unknown = value.filter((id) => !grantable.some((l) => l.id === id));
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-foreground">Locations</legend>
      {allowWhole && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={whole} disabled={disabled} onChange={(e) => onChange({ whole: e.target.checked, ids: value })} />
          Whole business
        </label>
      )}
      {!whole && (
        <div className={allowWhole ? 'ml-5 space-y-1' : 'space-y-1'}>
          {grantable.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={value.includes(l.id)} disabled={disabled} onChange={(e) => onChange({ whole: false, ids: e.target.checked ? [...value, l.id] : value.filter((id) => id !== l.id) })} />
              {l.name}
            </label>
          ))}
          {unknown.map((id) => (
            <label key={id} className="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked disabled={disabled} onChange={() => onChange({ whole: false, ids: value.filter((x) => x !== id) })} />
              {id} (no longer grantable — uncheck to drop)
            </label>
          ))}
          {value.length === 0 && <p className="text-xs text-danger-fg">Pick at least one location.</p>}
        </div>
      )}
    </fieldset>
  );
}

/** Exported for its own tests: the page needs `useMe` + PanelShell; the behaviour worth pinning is here. */
export function TeamPanel() {
  const businessId = useBusinessId() ?? '';
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInvitation | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [t, l] = await Promise.all([api.get<TeamResponse>(businessApi(businessId, 'team')), api.get<{ locations: LocationSummary[] }>(businessApi(businessId, 'l'))]);
      setTeam(t);
      setLocations(l.locations.map((x) => ({ id: x.id, name: x.name, status: x.status })));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [businessId]);

  useEffect(() => {
    if (businessId) void reload();
  }, [businessId, reload]);

  /** the action's own failure is the ONLY thing reported as a failure; a refresh hiccup afterwards never hides a committed result */
  async function act(label: string, fn: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(null);
    let ok = false;
    try {
      await fn();
      ok = true;
    } catch (err) {
      setError(`${label} failed: ${errorCode(err)}`);
    } finally {
      setBusy(false);
    }
    if (ok) await reload();
    return ok;
  }

  if (!team && loadFailed) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-text-secondary">Couldn&rsquo;t load the team.</p>
        <Button type="button" size="sm" variant="outline" onClick={() => void reload()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!team) return null;
  const { viewer } = team;
  const canInvite = viewer.role === 'owner' || viewer.role === 'manager';

  return (
    <div className="space-y-6">
      {error && (
        <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
          {error}
        </div>
      )}
      {/* always mounted so the one-time link is announced as a CHANGE inside a live region, and survives a refresh hiccup */}
      <div role="status" aria-live="polite">
        {created && <InviteLinkPanel created={created} onDone={() => setCreated(null)} />}
      </div>
      {loadFailed && (
        <p className="text-sm text-text-secondary">
          The list could not be refreshed.{' '}
          <button type="button" className="underline" onClick={() => void reload()}>
            Retry
          </button>
        </p>
      )}

      <section className="rounded-xl border border-border-subtle bg-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Members</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-text-secondary">
              {team.seats.occupied} of {team.seats.max} seats
            </span>
            {viewer.role === 'owner' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setTransferOpen(true);
                }}
              >
                Transfer ownership
              </Button>
            )}
          </div>
        </div>
        <ul className="mt-4 divide-y divide-border-subtle">
          {team.members.map((m) => {
            const controls = controlsFor(viewer, m, 'member', m.membershipId);
            return (
              <li key={m.membershipId} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    {m.name?.trim() || m.email}
                    {m.role === 'owner' && (
                      <Badge variant="secondary" className="ml-2">
                        Owner
                      </Badge>
                    )}
                  </div>
                  <p className="truncate text-xs text-text-secondary">
                    <span>{m.email}</span> · {m.role} · {scopeSummary(m.locationScope, locations)} · joined {new Date(m.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {controls.edit && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      aria-label={`Edit role and scope for ${m.email}`}
                      onClick={() => {
                        setError(null);
                        setEditing(m);
                      }}
                    >
                      Edit role &amp; scope
                    </Button>
                  )}
                  {controls.remove && (
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      aria-label={`Remove ${m.email}`}
                      onClick={() => {
                        if (!window.confirm(`Remove ${m.email} from this business?`)) return;
                        void act('Remove', () => api.delete(businessApi(businessId, `team/members/${m.membershipId}`)));
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="rounded-xl border border-border-subtle bg-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Pending invitations</h2>
          {canInvite && (
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => {
                setError(null);
                setInviteOpen(true);
              }}
            >
              Invite
            </Button>
          )}
        </div>
        {team.invitations.length === 0 ? (
          <p className="mt-3 text-sm text-text-secondary">No pending invitations.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border-subtle">
            {team.invitations.map((i) => {
              const controls = controlsFor(viewer, i, 'invitation');
              return (
                <li key={i.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{i.emailDisplay}</p>
                    <p className="truncate text-xs text-text-secondary">
                      {i.role} · {scopeSummary(i.locationScope, locations)} · expires {new Date(i.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  {controls.remove && (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Resend invitation to ${i.emailDisplay}`}
                        onClick={() =>
                          void act('Resend', async () => {
                            const next = await api.post<CreatedInvitation>(businessApi(businessId, `team/invitations/${i.id}/resend`), {});
                            setCreated(next);
                          })
                        }
                      >
                        Resend
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={busy}
                        aria-label={`Revoke invitation to ${i.emailDisplay}`}
                        onClick={() => {
                          if (!window.confirm(`Revoke the invitation for ${i.emailDisplay}?`)) return;
                          void act('Revoke', () => api.delete(businessApi(businessId, `team/invitations/${i.id}`)));
                        }}
                      >
                        Revoke
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {inviteOpen && (
        <InviteModal
          viewer={viewer}
          locations={locations}
          busy={busy}
          error={error}
          onClose={() => setInviteOpen(false)}
          onSubmit={(body) =>
            void act('Invite', async () => {
              const next = await api.post<CreatedInvitation>(businessApi(businessId, 'team/invitations'), body);
              setCreated(next);
              setInviteOpen(false);
            })
          }
        />
      )}
      {transferOpen && (
        <TransferOwnershipModal
          candidates={team.members.filter((m) => m.role !== 'owner' && m.membershipId !== viewer.membershipId)}
          busy={busy}
          error={error}
          onClose={() => setTransferOpen(false)}
          onSubmit={(targetMembershipId) =>
            void act('Transfer', async () => {
              await api.post(businessApi(businessId, 'team/transfer-ownership'), { targetMembershipId, confirmation: 'TRANSFER' });
              setTransferOpen(false);
              // the caller is no longer the owner: drop the pre-transfer list NOW (so a failed refresh can never
              // keep showing owner-only controls) and refresh the session/business context the shell derives from
              setTeam(null);
              invalidateMe();
            })
          }
        />
      )}
      {editing && (
        <EditMemberModal
          member={editing}
          locations={locations}
          busy={busy}
          error={error}
          onClose={() => setEditing(null)}
          onSubmit={(body) =>
            void act('Save', async () => {
              await api.patch(businessApi(businessId, `team/members/${editing.membershipId}`), body);
              setEditing(null);
            })
          }
        />
      )}
    </div>
  );
}

function ModalError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
      {error}
    </div>
  );
}

function InviteModal({ viewer, locations, busy, error, onClose, onSubmit }: { viewer: TeamResponse['viewer']; locations: LocationSummary[]; busy: boolean; error: string | null; onClose: () => void; onSubmit: (body: { email: string; role: 'manager' | 'editor'; locationScope: string[] }) => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'manager' | 'editor'>('editor');
  const restricted = viewer.role === 'manager' && viewer.locationScope.length > 0;
  // a restricted manager can only grant inside their own scope: start from it, and "whole business" is not on offer
  const [whole, setWhole] = useState(!restricted);
  const [scope, setScope] = useState<string[]>(restricted ? viewer.locationScope : []);
  const managerAllowed = viewer.role === 'owner';
  const scopeValid = whole || scope.length > 0;
  return (
    <Modal open onClose={onClose} title="Invite a teammate" size="md" disableClose={busy}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!scopeValid) return;
          onSubmit({ email: email.trim(), role, locationScope: whole ? [] : scope });
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="invite-email">Email</Label>
          <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={busy} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="invite-role">Role</Label>
          <select id="invite-role" className="block w-full rounded-md border border-input bg-surface-card px-3 py-2 text-sm" value={role} onChange={(e) => setRole(e.target.value as 'manager' | 'editor')} disabled={busy}>
            <option value="editor">Editor</option>
            {managerAllowed && <option value="manager">Manager</option>}
          </select>
        </div>
        <ScopePicker
          locations={restricted ? locations.filter((l) => viewer.locationScope.includes(l.id)) : locations}
          value={scope}
          whole={whole}
          allowWhole={!restricted}
          onChange={(next) => {
            setWhole(next.whole);
            setScope(next.ids);
          }}
          disabled={busy}
        />
        <ModalError error={error} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !email.trim() || !scopeValid}>
            Create invite link
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditMemberModal({ member, locations, busy, error, onClose, onSubmit }: { member: Member; locations: LocationSummary[]; busy: boolean; error: string | null; onClose: () => void; onSubmit: (body: { role: 'manager' | 'editor'; locationScope: string[] }) => void }) {
  const [role, setRole] = useState<'manager' | 'editor'>(member.role === 'manager' ? 'manager' : 'editor');
  const [whole, setWhole] = useState(member.locationScope.length === 0);
  const [scope, setScope] = useState<string[]>(member.locationScope);
  const scopeValid = whole || scope.length > 0;
  return (
    <Modal open onClose={onClose} title={`Edit ${member.email}`} size="md" disableClose={busy}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!scopeValid) return;
          onSubmit({ role, locationScope: whole ? [] : scope });
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="edit-role">Role</Label>
          <select id="edit-role" className="block w-full rounded-md border border-input bg-surface-card px-3 py-2 text-sm" value={role} onChange={(e) => setRole(e.target.value as 'manager' | 'editor')} disabled={busy}>
            <option value="editor">Editor</option>
            <option value="manager">Manager</option>
          </select>
        </div>
        <ScopePicker
          locations={locations}
          value={scope}
          whole={whole}
          allowWhole
          onChange={(next) => {
            setWhole(next.whole);
            setScope(next.ids);
          }}
          disabled={busy}
        />
        <ModalError error={error} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !scopeValid}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function Team() {
  const meState = useMe();
  if (meState.kind === 'error') {
    return (
      <div className="px-6 py-8">
        <PageError eyebrow="Account" title="Team" message={meState.message} retry={invalidateMe} />
      </div>
    );
  }
  if (meState.kind !== 'ready') return null;
  return (
    <PanelShell>
      <PageContainer>
        <PageHeader eyebrow="Account" title="Team" description="Who can work in this business, and what they can reach. Invitations are shared as a link you copy — nothing is e-mailed." />
        <TeamPanel />
      </PageContainer>
    </PanelShell>
  );
}

/**
 * P2.7 — hand INTERNAL pageboss ownership to an existing manager/editor. The action is immediate, there
 * is no recipient acceptance, the current owner becomes a Manager with full access, and — stated on
 * the form because it is the most common misunderstanding — Google Business Profile ownership is NOT
 * transferred by this (the advisory uses the glossary noun). The literal confirmation is intent, not authorization: the API re-proves the
 * owner inside the transaction.
 */
function TransferOwnershipModal({ candidates, busy, error, onClose, onSubmit }: { candidates: Member[]; busy: boolean; error: string | null; onClose: () => void; onSubmit: (targetMembershipId: string) => void }) {
  // no pre-selection: an irreversible, no-acceptance action needs an explicit choice
  const [target, setTarget] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const ready = target !== '' && confirmation === 'TRANSFER';
  return (
    <Modal open onClose={onClose} title="Transfer ownership" size="md" disableClose={busy}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          onSubmit(target);
        }}
      >
        {candidates.length === 0 ? (
          <p className="text-sm text-text-secondary">Invite a manager or editor first — ownership can only move to an existing member.</p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="transfer-target">New owner</Label>
            <select id="transfer-target" className="block w-full rounded-md border border-input bg-surface-card px-3 py-2 text-sm" value={target} onChange={(e) => setTarget(e.target.value)} disabled={busy}>
              <option value="" disabled>
                Choose a member…
              </option>
              {candidates.map((m) => (
                <option key={m.membershipId} value={m.membershipId}>
                  {(m.name?.trim() ? `${m.name.trim()} · ` : '') + m.email} · {m.role}
                </option>
              ))}
            </select>
          </div>
        )}
        <ul className="list-disc space-y-1 pl-5 text-sm text-text-secondary">
          <li>The selected person becomes the sole pageboss Owner of this business.</li>
          <li>You become a Manager with full access; you keep working here.</li>
          <li>This happens immediately — there is no acceptance step.</li>
          <li>
            <strong>{TERMS.googleProfile} ownership is not transferred.</strong> Google ownership stays exactly as it is.
          </li>
        </ul>
        <div className="space-y-1.5">
          <Label htmlFor="transfer-confirmation">Type TRANSFER to confirm</Label>
          <Input id="transfer-confirmation" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" disabled={busy} />
        </div>
        <ModalError error={error} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" disabled={busy || !ready}>
            Transfer ownership
          </Button>
        </div>
      </form>
    </Modal>
  );
}
